import React, { useMemo } from 'react';
import { CustomAvatar } from '@/components/Hypertracker/AsideMenu/AsideMenuStyled';
import { Typography, useTheme } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { selectMemberProfile } from '@/state/member/memberSelectors';
import {
    AlertsMobileIcon,
    ApiMobileIcon,
    BuildersMobileIcon,
    CohortMobileIcon,
    DashboardMobileIcon,
    FavoritesMobileIcon,
    FlowsIcon,
    HeatmapMobileIcon,
    HipIcon,
    HomeMobileIcon,
    LeaderboardMobileIcon,
    LiquidationsMobileIcon,
    MarketRadarMobileIcon,
    PerpsMobileIcon,
    StatsMobileIcon,
} from '@/components/Hypertracker/HypertrackerSidebar/HypertrackerSidebarStyled';
import useIsMemberAuthorized from '@/hooks/useIsMemberAuthorized';
import { useQueryFetcher } from '@/lib/query/useQueryFetcher';
import { AlertsStatsResponse } from '@/lib/types/hypertrackerTypes';
import { nFormatter } from '@/lib/helpers/helpers';
import { HT_API_ROUTES } from '@/lib/http/hypertrackerHttpRequest';
import { selectTelegramConnectionHT } from '@/state/hypertracker/hypertrackerSelector';

const AsideMenuItem = ({ menuItem, isSelected, t }) => {
    const member = useSelector((state: RootState) => selectMemberProfile(state));
    const theme = useTheme();
    const isMemberAuthorized = useIsMemberAuthorized();
    const telegramAccountData = useSelector((state: RootState) =>
        selectTelegramConnectionHT(state),
    );
    const avatarLetter = useMemo(() => {
        if (member?.firstName) {
            return member.firstName[0].toUpperCase();
        } else if (member?.username) {
            return member.username[0].toUpperCase();
        } else return '';
    }, [member]);

    const alertsStatsQuery = useQueryFetcher<AlertsStatsResponse>({
        mode: 'api',
        apiPath: HT_API_ROUTES.ALERTS.STATS,
        isAuthorized: isMemberAuthorized && !!telegramAccountData,
    });

    const totalActiveAlertsFormatted = useMemo(() => {
        return nFormatter(alertsStatsQuery?.data?.totalActiveAlerts || 0);
    }, [alertsStatsQuery.data]);

    const getIcon = (iconName: string, isSelected) => {
        const color = isSelected
            ? theme.palette.hypertracker.light
            : theme.palette.primitives.woodsmoke[500];

        switch (iconName) {
            case 'menu_home':
                return <HomeMobileIcon />;
            case 'menu_heatmap':
                return <HeatmapMobileIcon />;
            case 'menu_cohorts':
                return <CohortMobileIcon />;
            case 'menu_leaderboard':
                return <LeaderboardMobileIcon />;
            case 'menu_perps':
                return <PerpsMobileIcon />;
            case 'menu_stats':
                return <StatsMobileIcon />;
            case 'menu_builders':
                return <BuildersMobileIcon />;
            case 'menu_alerts':
                return <AlertsMobileIcon />;
            case 'menu_api':
                return <ApiMobileIcon />;
            case 'menu_favorites':
                return <FavoritesMobileIcon />;
            case 'menu_flows':
                return <FlowsIcon sx={{ path: { stroke: color } }} />;
            case 'menu_liquidations':
                return <LiquidationsMobileIcon />;
            case 'menu_radar':
                return <MarketRadarMobileIcon />;
            case 'menu_hip3':
                return <HipIcon sx={{ path: { fill: color } }} />;
            case 'menu_cmm_dashboard':
                return (
                    <DashboardMobileIcon
                        sx={{ path: { fill: theme.palette.primitives.blueribbon[500] } }}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Stack
            direction={'row'}
            width={'100%'}
            gap={'8px'}
            alignItems={'center'}
            justifyContent={menuItem.id === 'menu_api' ? 'center' : 'space-between'}
        >
            <Box display={'flex'} gap={'8px'} alignItems={'center'}>
                {menuItem.id === 'avatar' ? (
                    <CustomAvatar>{avatarLetter}</CustomAvatar>
                ) : (
                    getIcon(menuItem.id, isSelected)
                )}
                <Typography
                    component="span"
                    variant={'p14'}
                    fontWeight={menuItem.id === 'menu_api' ? 700 : 600}
                    color={
                        isSelected ? 'hypertracker.light' : theme.palette.primitives.woodsmoke[500]
                    }
                >
                    {menuItem.id === 'avatar'
                        ? menuItem.label
                        : t(`hypertracker_sidebar.${menuItem.label}`)}
                </Typography>
            </Box>

            {menuItem.label === 'menu_alerts' &&
                isMemberAuthorized &&
                +totalActiveAlertsFormatted > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '2px 8px',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            borderRadius: '8px',
                            opacity: 0.7,
                        }}
                    >
                        <Typography
                            variant={'p10'}
                            color={'customText.text1'}
                            fontFamily={'"IBM Plex Mono", monospace'}
                        >
                            {totalActiveAlertsFormatted}
                        </Typography>
                    </Box>
                )}
        </Stack>
    );
};

export default AsideMenuItem;
